import React, { useEffect, useId, useMemo } from 'react';
import { MantineProvider, createTheme } from '@mantine/core';
import ActionTooltips from './ActionTooltips';
import { RefinedUIContext } from './RefinedUIContext';

const activeRefinedProviders = new Set();

const fontFamily = '"InterVariable", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const sharedTheme = createTheme({
  fontFamily,
  headings: { fontFamily, fontWeight: '650' },
  primaryColor: 'charcoal',
  primaryShade: 7,
  defaultRadius: 'md',
  focusRing: 'auto',
  respectReducedMotion: true,
  cursorType: 'pointer',
  colors: {
    charcoal: ['#f5f5f4', '#e7e7e5', '#d0d1cf', '#afb1ae', '#8c8f8b', '#686c68', '#454946', '#272b28', '#1b1e1c', '#111412'],
    terracotta: ['#fcf1ee', '#f4e0da', '#e6bdb1', '#d69785', '#c77660', '#b75f47', '#a34b36', '#963830', '#7d2c26', '#63221e'],
  },
  radius: { xs: '4px', sm: '6px', md: '10px', lg: '16px', xl: '16px' },
  components: {
    Button: {
      defaultProps: { size: 'md' },
      styles: { root: { '--button-height': '48px', fontWeight: 600, letterSpacing: '-0.01em' } },
    },
    ActionIcon: { defaultProps: { size: 48, variant: 'default' } },
    Input: {
      defaultProps: { size: 'md' },
      styles: {
        wrapper: { '--input-height': '48px', '--input-fz': '16px' },
      },
    },
    InputWrapper: {
      styles: { label: { fontSize: '0.875rem', fontWeight: 500, marginBottom: 7, color: '#4e5350' } },
    },
    TextInput: { defaultProps: { size: 'md' } },
    NativeSelect: { defaultProps: { size: 'md' } },
    Select: {
      defaultProps: { size: 'md' },
      styles: { option: { minHeight: 48, display: 'flex', alignItems: 'center' } },
    },
    Combobox: {
      defaultProps: { withinPortal: true, zIndex: 1100, shadow: 'md', radius: 'md' },
      styles: { option: { minHeight: 48, display: 'flex', alignItems: 'center' } },
    },
    Menu: {
      defaultProps: { withinPortal: true, zIndex: 1100, shadow: 'md', radius: 'md', offset: 6 },
      styles: { item: { minHeight: 48, fontSize: '0.9375rem' }, dropdown: { padding: 6 } },
    },
    Popover: { defaultProps: { withinPortal: true, zIndex: 1100, shadow: 'md', radius: 'md' } },
    Pagination: { defaultProps: { size: 48, radius: 'md' } },
  },
});

const cssVariablesResolver = () => ({
  variables: {},
  light: {
    '--mantine-color-text': '#242728',
    '--mantine-color-dimmed': '#696f6b',
    '--mantine-color-default-border': '#d0d1cf',
    '--mantine-color-default-hover': '#f1f3f5',
    '--mantine-color-placeholder': '#696f6b',
    '--mantine-color-body': '#f6f7f9',
  },
  dark: {},
});

// The legacy application keeps its own CSS, colors, and layout. Mantine's
// variables and portal contents belong only to the opted-in shell.
export default function RefinedUIProvider({ enabled, children }) {
  const instanceId = useId().replace(/:/g, '');
  const portalId = `refined-mantine-portals-${instanceId}`;
  useEffect(() => {
    if (enabled) activeRefinedProviders.add(instanceId);
    document.body.classList.toggle('app-site-refined', activeRefinedProviders.size > 0);
    return () => {
      activeRefinedProviders.delete(instanceId);
      document.body.classList.toggle('app-site-refined', activeRefinedProviders.size > 0);
    };
  }, [enabled, instanceId]);
  const theme = useMemo(() => createTheme({
    ...sharedTheme,
    components: {
      ...sharedTheme.components,
      Portal: { defaultProps: { target: `#${portalId}` } },
    },
  }), [portalId]);

  // Keep the provider's identity stable when the pilot is toggled so page
  // state (including unsaved form values) survives switching to the old UI.
  // Variables remain scoped to the opted-in shell and its portal host.
  return (
    <MantineProvider theme={theme} forceColorScheme="light"
      cssVariablesSelector=":where(.app--refined, .refined-mantine-portals)"
      cssVariablesResolver={cssVariablesResolver}
      getRootElement={() => typeof document === 'undefined' ? undefined : document.querySelector('.app--refined')}>
      <RefinedUIContext.Provider value={Boolean(enabled)}>
        {children}
        <ActionTooltips enabled={enabled} portalId={portalId} />
        <div id={portalId} className={enabled ? 'refined-mantine-portals' : undefined} data-mantine-color-scheme="light" />
      </RefinedUIContext.Provider>
    </MantineProvider>
  );
}
